import React, {
  forwardRef,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  FolderOpenOutlined,
  LinkOutlined,
  PrinterOutlined,
  UnorderedListOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import unifiedProductService from '../../services/unifiedProductService';
import { usePrintQueue } from '../../context/PrintQueueContext';
import { useAuth } from '../../context/AuthContext';
import { findGiftCardCandidates } from '../../utils/giftCardCandidates';
import { analyzePrintSeparatorGroups } from '../../utils/printJobSeparators';
import { printHTMLSilent } from '../../utils/silentPrintService';
import {
  getQzPrintReadiness,
  loadQzPrintSettings,
  printHtmlBatchWithQz,
} from '../../services/qzPrintService';
import {
  getActivePrintWaveRecipe,
  loadPrintWaveRecipeState,
  savePrintWaveRecipeState,
} from '../../services/printWaveRecipeStore';
import { buildWavePickList, describeWaveStack, getWaveOrderKey } from '../../utils/wavePickListModel';
import { buildWavePickListHtml } from '../../utils/wavePickListHtml';
import { createStandardPackingSlipMargins } from '../../utils/packingSlipHtml';
import {
  preferenceDocIdForOrder,
  resolveBatchPackingSlipDefaults,
} from '../../utils/packingSlipPrintDefaults';
import { loadPackingSlipPrintPreferencesForOrders } from '../../services/packingSlipPrintPreferenceService';
import {
  extractShippingLabelPdfPages,
  matchShippingLabelPagesToOrders,
} from '../../services/shippingLabelPdfMatchService';
import { getDriveShareLink } from '../../services/operatorDriveService';
import {
  formatDriveStorageCorsMessage,
  loadOperatorDriveItemBlob,
} from '../../utils/operatorDriveShareDownload';
import { stageMatchedShippingLabelPdfToQueue } from '../../utils/stageMatchedShippingLabelPdfToQueue';
import { stageTeamCarrierAttaches } from '../../utils/stageTeamCarrierAttaches';
import {
  SHIPPING_LABEL_ATTACH_STATUS,
  findShippingLabelAttachByDriveItem,
  saveShippingLabelAttach,
} from '../../services/shippingLabelAttachService';
import { deletePrintQueueBlob, putPrintQueueBlob } from '../../services/printQueueStore';
import { shipLabelSourceBlobRef } from '../../utils/shippingLabelPreview';
import OperatorDrivePicker from '../operatorDrive/OperatorDrivePicker';
import './WaveDeskPanel.css';

export const WAVE_COMMAND_LABELS = Object.freeze({
  printWave: 'Print wave',
  assembleDay: 'Assemble day stack',
  assembleAndStart: 'Assemble and start QZ',
  printPickList: 'Print pick list',
  queuePickList: 'Queue pick list',
});

const MAX_CARRIER_PDF_BYTES = 50 * 1024 * 1024;

function WaveDeskPanel(
  {
    orders = [],
    folderLabel = '',
    folderId = '',
    workingDeliveryDate = '',
    isPacked = () => false,
    isShipped = () => false,
    teamAttaches = [],
    onOpenPrePrint,
    onOpenQueue,
    onTeamAttachSaved,
    showCommandBar = true,
  },
  ref,
) {
  const { user } = useAuth();
  const printQueue = usePrintQueue();
  const [recipeState, setRecipeState] = useState(() => loadPrintWaveRecipeState());
  const [busy, setBusy] = useState('');
  const busyRef = useRef('');
  const [status, setStatus] = useState('');
  const [carrierAttachments, setCarrierAttachments] = useState([]);
  const [drivePickerOpen, setDrivePickerOpen] = useState(false);
  const recipe = getActivePrintWaveRecipe(recipeState);

  const wave = useMemo(
    () =>
      buildWavePickList(orders, {
        folderLabel,
        formatName: (sku, fallback) => unifiedProductService.getProductName?.(sku) || fallback || sku,
        formatPack: (sku) => unifiedProductService.getProductPackSize?.(sku) || '',
        isPacked,
        isShipped,
      }),
    [orders, folderLabel, isPacked, isShipped],
  );
  const stack = useMemo(() => describeWaveStack(wave, recipe), [wave, recipe]);
  const giftCandidates = useMemo(() => findGiftCardCandidates(orders), [orders]);
  const giftCount = useMemo(
    () => giftCandidates.filter((candidate) => candidate.likely).length,
    [giftCandidates],
  );
  const mixedRuns = analyzePrintSeparatorGroups(orders).hasMultiple;
  const matchedCarrierCount = useMemo(
    () =>
      carrierAttachments.reduce(
        (total, attachment) =>
          total +
          Object.values(attachment.pageAssignments || {}).filter((value) =>
            String(value || '').trim(),
          ).length,
        0,
      ),
    [carrierAttachments],
  );
  const carrierPageCount = useMemo(
    () =>
      carrierAttachments.reduce(
        (total, attachment) => total + (Number(attachment.pageCount) || 0),
        0,
      ),
    [carrierAttachments],
  );

  const patchRecipe = (partial) => {
    const nextRecipes = recipeState.recipes.map((entry) =>
      entry.id === recipe.id ? { ...entry, ...partial } : entry,
    );
    const next = savePrintWaveRecipeState({ ...recipeState, recipes: nextRecipes });
    setRecipeState(next);
  };

  const pickHtml = () => buildWavePickListHtml(wave, { stack });

  const clearAllCarriers = async () => {
    await Promise.all(
      carrierAttachments.map((attachment) =>
        deletePrintQueueBlob(attachment.sourceRef).catch(() => {}),
      ),
    );
    setCarrierAttachments([]);
  };

  const removeCarrierAttachment = async (attachmentId) => {
    const target = carrierAttachments.find((entry) => entry.id === attachmentId);
    if (target?.sourceRef) {
      await deletePrintQueueBlob(target.sourceRef).catch(() => {});
    }
    setCarrierAttachments((previous) => previous.filter((entry) => entry.id !== attachmentId));
  };

  const attachmentAssignedCount = (attachment) =>
    Object.values(attachment?.pageAssignments || {}).filter((value) =>
      String(value || '').trim(),
    ).length;

  const attachmentNeedsAlign = (attachment) =>
    (attachment?.pageMatches || []).some((page) => {
      const assigned = String(attachment.pageAssignments?.[page.pageNumber] || '').trim();
      if (assigned) return false;
      return Boolean(
        String(page.bestOrderKey || page.matches?.[0]?.orderKey || '').trim(),
      );
    });

  const attachmentFullyAssigned = (attachment) => {
    const pages = attachment?.pageMatches || [];
    if (!pages.length) return false;
    return pages.every((page) =>
      String(attachment.pageAssignments?.[page.pageNumber] || '').trim(),
    );
  };

  const alignAttachmentToBestMatches = (attachmentId) => {
    setCarrierAttachments((previous) =>
      previous.map((entry) => {
        if (entry.id !== attachmentId) return entry;
        const nextAssignments = { ...(entry.pageAssignments || {}) };
        let filled = 0;
        (entry.pageMatches || []).forEach((page) => {
          const current = String(nextAssignments[page.pageNumber] || '').trim();
          if (current) return;
          const best = String(
            page.bestOrderKey || page.matches?.[0]?.orderKey || '',
          ).trim();
          if (!best) return;
          nextAssignments[page.pageNumber] = best;
          filled += 1;
        });
        if (filled) {
          setStatus(
            `Aligned ${filled} page${filled === 1 ? '' : 's'} to best match. Save team attach to update Pack list chips.`,
          );
        } else {
          setStatus('No best matches left to align on that PDF.');
        }
        return { ...entry, pageAssignments: nextAssignments };
      }),
    );
  };

  const saveAttachmentAsTeamAttach = async (attachmentId) => {
    const attachment = carrierAttachments.find((entry) => entry.id === attachmentId);
    if (!attachment) return;
    if (!attachmentFullyAssigned(attachment)) {
      setStatus(
        'Assign every page (or Align to best matches) before saving the team attach.',
      );
      return;
    }
    if (!user?.uid) {
      setStatus('Sign in to save a team attach.');
      return;
    }
    setBusy('attach');
    setStatus(`Saving team attach for “${attachment.name}”…`);
    try {
      const pages = (attachment.pageMatches || []).map((page) => {
        const orderKey = String(
          attachment.pageAssignments?.[page.pageNumber] || '',
        ).trim();
        return {
          pageNumber: page.pageNumber,
          orderKey,
          status:
            page.suggestedOrderKey === orderKey
              ? 'matched'
              : page.bestOrderKey === orderKey
                ? 'matched'
                : 'manual',
          matchSummary: page.matchSummary,
          textPreview: page.textPreview,
        };
      });
      const existing = attachment.driveItemId
        ? await findShippingLabelAttachByDriveItem(attachment.driveItemId)
        : null;
      const saved = await saveShippingLabelAttach({
        id: existing?.id,
        driveItemId: attachment.driveItemId || null,
        fileName: attachment.name,
        pageCount: pages.length,
        workingDeliveryDate: workingDeliveryDate || folderLabel,
        folderId: folderId || null,
        folderLabel,
        pages,
        status: SHIPPING_LABEL_ATTACH_STATUS.ATTACHED,
        attachedByUid: user.uid,
        attachedByName: user.displayName || user.email || '',
      });
      if (attachment.blob instanceof Blob && saved?.id) {
        await putPrintQueueBlob(shipLabelSourceBlobRef(saved.id), attachment.blob, {
          name: attachment.name,
          attachId: saved.id,
        }).catch(() => {});
      }
      setStatus(
        `Team attach saved for “${attachment.name}”. Pack list chips should show the shipping label.`,
      );
      onTeamAttachSaved?.(saved);
    } catch (error) {
      setStatus(error?.message || 'Could not save team attach.');
    } finally {
      setBusy('');
    }
  };

  const loadCarrierBlob = async (blob, name, meta = {}) => {
    if (!(blob instanceof Blob) || !blob.size) {
      throw new Error('A shipping-label PDF file is required.');
    }
    if (blob.size > MAX_CARRIER_PDF_BYTES) {
      throw new Error('Shipping-label PDF exceeds the 50 MB safety limit.');
    }
    setBusy('carrier');
    setStatus(`Parsing “${name}”…`);
    try {
      const pages = await extractShippingLabelPdfPages(blob);
      const matches = matchShippingLabelPagesToOrders(pages, orders, {
        workingDeliveryDate: workingDeliveryDate || folderLabel,
      });
      const sourceBlobRef = `wave-carrier-${globalThis.crypto?.randomUUID?.() || Date.now()}`;
      await putPrintQueueBlob(sourceBlobRef, blob, { temporary: true, name });
      const assignments = {};
      matches.forEach((page) => {
        assignments[page.pageNumber] = page.suggestedOrderKey || '';
      });
      const attachment = {
        id: sourceBlobRef,
        name,
        pageCount: pages.length,
        sourceRef: sourceBlobRef,
        blob,
        driveItemId: meta.driveItemId || '',
        pageMatches: matches,
        pageAssignments: assignments,
      };
      setCarrierAttachments((previous) => [...previous, attachment]);
      const auto = matches.filter((page) => page.suggestedOrderKey).length;
      const alignable = matches.filter(
        (page) =>
          !page.suggestedOrderKey &&
          String(page.bestOrderKey || page.matches?.[0]?.orderKey || '').trim(),
      ).length;
      if (auto === pages.length && pages.length > 0) {
        setStatus(
          `Added “${name}”: ${pages.length} page${pages.length === 1 ? '' : 's'} auto-matched. Save team attach to align Pack list chips, or Assemble day stack to print.`,
        );
      } else if (alignable > 0) {
        setStatus(
          `Added “${name}”: ${auto} auto-matched · ${alignable} have a best match — use Align to best matches, then Save team attach.`,
        );
      } else {
        setStatus(
          `Added “${name}”: ${pages.length} carrier page${pages.length === 1 ? '' : 's'} · ${auto} auto-matched. Attach more day PDFs if needed, then Assemble day stack.`,
        );
      }
      if (!recipe.includeCarrierLabels) {
        patchRecipe({ includeCarrierLabels: true });
      }
    } finally {
      setBusy('');
    }
  };

  const loadCarrierFromDrive = async (items) => {
    const list = Array.isArray(items) ? items.filter(Boolean) : [];
    if (!list.length) return;
    setBusy('carrier');
    setStatus(`Loading ${list.length} Smoke Drive PDF${list.length === 1 ? '' : 's'}…`);
    try {
      for (const item of list) {
        const { blob, fileName } = await loadOperatorDriveItemBlob(item, {
          resolveDownloadUrl: async (driveItem) => getDriveShareLink(driveItem.id),
        });
        await loadCarrierBlob(blob, fileName, { driveItemId: item.id });
      }
    } catch (error) {
      setStatus(formatDriveStorageCorsMessage('wave', error));
      setBusy('');
    }
  };

  const printPickListNow = async () => {
    if (!wave.orderCount || busyRef.current) return;
    busyRef.current = 'pick';
    setBusy('pick');
    setStatus('');
    try {
      const html = pickHtml();
      const readiness = await getQzPrintReadiness({ force: true });
      if (readiness.ready) {
        const qzSettings = loadQzPrintSettings();
        await printHtmlBatchWithQz([
          {
            profileKey: 'standard',
            label: 'Wave pick list',
            html,
            settings: qzSettings.standard,
          },
        ]);
        setStatus('Pick list submitted to Tray 1. Check the physical sheet, then print the wave.');
      } else {
        await printHTMLSilent(html);
        setStatus('Browser print dialog closed for the pick list. Check the sheet, then print the wave.');
      }
    } catch (error) {
      setStatus(error?.message || 'Pick list could not be printed.');
    } finally {
      busyRef.current = '';
      setBusy('');
    }
  };

  const queuePickList = () => {
    if (!wave.orderCount || busyRef.current) return;
    printQueue.enqueueWavePickList({
      html: pickHtml(),
      folderLabel,
      qzSettings: loadQzPrintSettings(),
      source: 'wave-desk',
    });
    setStatus('Pick list is first in the mixed queue. Add the rest of the wave or start the queue.');
    onOpenQueue?.();
  };

  const printThisWave = () => {
    if (!wave.orderCount || busyRef.current) return;
    if (recipe.includePickList) {
      printQueue.enqueueWavePickList({
        html: pickHtml(),
        folderLabel,
        qzSettings: loadQzPrintSettings(),
        source: 'wave-desk',
      });
    }
    const formatHint =
      recipe.format === 'cheetah' ||
      recipe.format === 'standard' ||
      recipe.format === 'go4'
        ? recipe.format
        : null;
    onOpenPrePrint?.(orders, formatHint, {
      paperSize: 'A4',
      orientation: 'portrait',
      margins: createStandardPackingSlipMargins(),
      showPrices: recipe.showPrices === true,
      showSignatureLines: recipe.showSignatureLines !== false,
      includeDuplicate: recipe.format !== 'cheetah',
    });
    setStatus(
      recipe.includePickList
        ? recipe.format === 'auto'
          ? 'Pick list queued first. Prepare print will pick Standard / Cheetah / GO4 per order — confirm before Print now. Carrier PDFs use Assemble day stack.'
          : 'Pick list queued first. Confirm packing slips in Prepare print job — inscriptions stay optional there. Carrier PDFs use Assemble day stack.'
        : 'Confirm packing slips in Prepare print job.',
    );
  };

  const attachedTeam = useMemo(
    () =>
      (teamAttaches || []).filter(
        (entry) => entry.status === SHIPPING_LABEL_ATTACH_STATUS.ATTACHED,
      ),
    [teamAttaches],
  );

  const assembleDayStack = async ({ startImmediately = false } = {}) => {
    if (!wave.orderCount || busyRef.current) return;
    if (recipe.includeCarrierLabels) {
      const hasLocal = carrierAttachments.length > 0;
      const hasTeam = attachedTeam.length > 0;
      if (!hasLocal && !hasTeam) {
        setStatus(
          'Attach a shipping-label PDF here, or save a team attach from Smoke Drive, before assembling carriers.',
        );
        return;
      }
      if (hasLocal) {
        const unassigned = carrierAttachments.some((attachment) =>
          (attachment.pageMatches || []).some(
            (page) => !String(attachment.pageAssignments?.[page.pageNumber] || '').trim(),
          ),
        );
        if (unassigned) {
          setStatus('Assign every local carrier page to an order before assembling the day stack.');
          return;
        }
      }
    }

    busyRef.current = 'assemble';
    setBusy('assemble');
    setStatus('Assembling day stack into the mixed print queue…');
    try {
      const qzSettings = loadQzPrintSettings();
      if (recipe.includePickList) {
        printQueue.enqueueWavePickList({
          html: pickHtml(),
          folderLabel,
          qzSettings,
          source: 'wave-day-stack',
        });
      }

      const giftCards = recipe.includeInscriptions
        ? giftCandidates
            .filter((candidate) => candidate.likely)
            .map((candidate) => ({
              orderKey: candidate.orderKey,
              order: candidate.order,
              message: candidate.message,
              sourceLabel: candidate.sourceLabel,
            }))
        : [];

      let formatByOrder = {};
      let copiesByOrder = {};
      let standardCount = 0;
      let cheetahCount = 0;
      let go4Count = 0;
      const formatLocked =
        recipe.format === 'cheetah' ||
        recipe.format === 'standard' ||
        recipe.format === 'go4';
      try {
        const preferences = await loadPackingSlipPrintPreferencesForOrders(orders);
        const smart = resolveBatchPackingSlipDefaults(
          orders,
          preferences,
          preferenceDocIdForOrder,
        );
        copiesByOrder = { ...(smart.copiesByOrder || {}) };
        orders.forEach((order, index) => {
          const key = getWaveOrderKey(order);
          if (!key) return;
          const entry = smart.perOrder?.[index];
          const orderFormat = formatLocked
            ? recipe.format
            : entry?.format === 'cheetah'
              ? 'cheetah'
              : entry?.format === 'go4'
                ? 'go4'
                : 'standard';
          formatByOrder[key] = orderFormat;
          if (!copiesByOrder[key] && entry?.copies) {
            copiesByOrder[key] = entry.copies;
          }
          if (orderFormat === 'cheetah') cheetahCount += 1;
          else if (orderFormat === 'go4') go4Count += 1;
          else standardCount += 1;
        });
      } catch (error) {
        console.warn('[WaveDesk] smart packing defaults failed:', error);
      }
      if (!Object.keys(formatByOrder).length) {
        orders.forEach((order) => {
          const key = getWaveOrderKey(order);
          if (!key) return;
          const orderFormat = formatLocked ? recipe.format : 'standard';
          formatByOrder[key] = orderFormat;
        });
        standardCount = Object.values(formatByOrder).filter((value) => value === 'standard').length;
        cheetahCount = Object.values(formatByOrder).filter((value) => value === 'cheetah').length;
        go4Count = Object.values(formatByOrder).filter((value) => value === 'go4').length;
      }

      printQueue.enqueuePackingSetup({
        orders,
        format: formatLocked ? recipe.format : 'standard',
        formatByOrder,
        copiesByOrder,
        settings: {
          margins: createStandardPackingSlipMargins(),
          showPrices: recipe.showPrices === true,
          showSignatureLines: recipe.showSignatureLines !== false,
          includeDuplicate: true,
        },
        qzSettings,
        giftCards,
        includeSeparators: recipe.includeSeparators !== false && mixedRuns,
        source: 'wave-day-stack',
      });

      let carrierStaged = 0;
      if (recipe.includeCarrierLabels) {
        const preferredOrderKeys = orders.map((order) => getWaveOrderKey(order)).filter(Boolean);
        if (attachedTeam.length) {
          const teamStaged = await stageTeamCarrierAttaches({
            attaches: attachedTeam,
            orders,
            workingDeliveryDate: workingDeliveryDate || folderLabel,
            enqueueCarrierLabel: printQueue.enqueueCarrierLabel,
          });
          carrierStaged += teamStaged.stagedLabels;
        }
        for (const attachment of carrierAttachments) {
          const staged = await stageMatchedShippingLabelPdfToQueue({
            sourceBlob: attachment.blob,
            pageMatches: attachment.pageMatches,
            pageAssignments: attachment.pageAssignments,
            candidateOrders: orders,
            workingDeliveryDate: workingDeliveryDate || folderLabel,
            preferredOrderKeys,
            copies: 1,
            enqueueCarrierLabel: printQueue.enqueueCarrierLabel,
          });
          carrierStaged += staged.orderCount;
          if (attachment.driveItemId) {
            const pages = (attachment.pageMatches || []).map((page) => ({
              pageNumber: page.pageNumber,
              orderKey: attachment.pageAssignments?.[page.pageNumber] || '',
              status: 'manual',
              matchSummary: page.matchSummary,
              textPreview: page.textPreview,
            }));
            const existing = await findShippingLabelAttachByDriveItem(attachment.driveItemId);
            await saveShippingLabelAttach({
              id: existing?.id,
              driveItemId: attachment.driveItemId,
              fileName: attachment.name,
              pageCount: pages.length,
              workingDeliveryDate: workingDeliveryDate || folderLabel,
              folderLabel,
              pages,
              status: SHIPPING_LABEL_ATTACH_STATUS.ATTACHED,
              attachedByUid: user?.uid || null,
              attachedByName: user?.displayName || user?.email || '',
            }).catch(() => {});
          }
        }
      }

      onOpenQueue?.();
      const paperBits = [];
      if (standardCount) paperBits.push(`${standardCount} Standard`);
      if (cheetahCount) paperBits.push(`${cheetahCount} Cheetah`);
      if (go4Count) paperBits.push(`${go4Count} GO4`);
      const paperNote = paperBits.length
        ? ` · ${paperBits.join(' + ')}`
        : ` · ${orders.length} Standard`;
      const carrierNote = carrierStaged
        ? ` · ${carrierStaged} carrier label${carrierStaged === 1 ? '' : 's'}`
        : '';
      setStatus(
        `Day stack staged: pick list, packing slips${paperNote}${
          giftCards.length ? `, ${giftCards.length} inscription${giftCards.length === 1 ? '' : 's'}` : ''
        }${carrierNote}. Drag bundles in the queue to fine-tune pickup order.`,
      );
      if (startImmediately) {
        await printQueue.startQueue();
        setStatus((previous) => `${previous} QZ started.`);
      }
    } catch (error) {
      setStatus(error?.message || 'Could not assemble the day stack.');
    } finally {
      busyRef.current = '';
      setBusy('');
    }
  };

  useImperativeHandle(ref, () => ({
    printWave: printThisWave,
    assembleDayStack: () => assembleDayStack({ startImmediately: false }),
    assembleAndStart: () => assembleDayStack({ startImmediately: true }),
    printPickList: printPickListNow,
    queuePickList,
    hasOrders: () => wave.orderCount > 0,
    busy,
  }));

  return (
    <section className="wave-desk">
      <header className="wave-desk__header">
        <p className="wave-desk__eyebrow">Beat the shipper desk</p>
        <h3>
          <UnorderedListOutlined /> Wave desk
        </h3>
        <p>
          {wave.orderCount} order{wave.orderCount === 1 ? '' : 's'} · {wave.skuCount} SKU
          {wave.skuCount === 1 ? '' : 's'} · {wave.unitCount} units
          {giftCount ? ` · ${giftCount} likely inscription${giftCount === 1 ? '' : 's'}` : ''}
          {mixedRuns ? ' · mixed shipping / dates' : ''}
          {carrierAttachments.length
            ? ` · ${carrierAttachments.length} day PDF${carrierAttachments.length === 1 ? '' : 's'} · ${carrierPageCount} carrier page${carrierPageCount === 1 ? '' : 's'} (${matchedCarrierCount} assigned)`
            : attachedTeam.length
              ? ` · ${attachedTeam.length} team attach${attachedTeam.length === 1 ? '' : 'es'}`
              : ''}
        </p>
      </header>

      <div className="wave-desk__recipe">
        <label>
          Saved recipe
          <select
            value={recipe.id}
            onChange={(event) => {
              const next = savePrintWaveRecipeState({
                ...recipeState,
                activeRecipeId: event.target.value,
              });
              setRecipeState(next);
            }}
          >
            {recipeState.recipes.map((entry) => (
              <option key={entry.id} value={entry.id}>
                {entry.name}
              </option>
            ))}
          </select>
        </label>
        <label className="wave-desk__check">
          <input
            type="radio"
            name="wave-format"
            checked={recipe.format === 'auto'}
            onChange={() => patchRecipe({ format: 'auto' })}
          />
          Auto paper (by method)
        </label>
        <label className="wave-desk__check">
          <input
            type="radio"
            name="wave-format"
            checked={recipe.format === 'standard'}
            onChange={() => patchRecipe({ format: 'standard' })}
          />
          Standard A4 only
        </label>
        <label className="wave-desk__check">
          <input
            type="radio"
            name="wave-format"
            checked={recipe.format === 'cheetah'}
            onChange={() => patchRecipe({ format: 'cheetah' })}
          />
          Cheetah LZ17 only
        </label>
        <label className="wave-desk__check">
          <input
            type="radio"
            name="wave-format"
            checked={recipe.format === 'go4'}
            onChange={() => patchRecipe({ format: 'go4' })}
          />
          GO4 only (A4 + GoDEX outside)
        </label>
        <label className="wave-desk__check">
          <input
            type="checkbox"
            checked={recipe.includePickList}
            onChange={(event) => patchRecipe({ includePickList: event.target.checked })}
          />
          Pick list first
        </label>
        <label className="wave-desk__check">
          <input
            type="checkbox"
            checked={recipe.includeInscriptions}
            onChange={(event) => patchRecipe({ includeInscriptions: event.target.checked })}
          />
          Inscriptions
        </label>
        <label className="wave-desk__check">
          <input
            type="checkbox"
            checked={recipe.includeCarrierLabels}
            onChange={(event) => patchRecipe({ includeCarrierLabels: event.target.checked })}
          />
          Carrier labels
        </label>
        <label className="wave-desk__check">
          <input
            type="checkbox"
            checked={recipe.includeSeparators}
            onChange={(event) => patchRecipe({ includeSeparators: event.target.checked })}
          />
          Separators when mixed
        </label>
      </div>

      <ol className="wave-desk__stack" aria-label="Physical stack">
        {stack.map((step, index) => (
          <li key={step.key}>
            <span>{index + 1}</span>
            <div>
              <b>{step.label}</b>
              <small>{step.detail}</small>
            </div>
          </li>
        ))}
      </ol>

      <div className="wave-desk__carriers">
        <div className="wave-desk__carriers-head">
          <div>
            <b>Day shipping labels</b>
            <p>
              Attach every day carrier PDF (XAM, T24, etc.) via upload or Smoke Drive.
              Pages are parsed and matched to this folder&apos;s orders — not by pasteable
              links. Assemble merges slips, inscriptions, and carriers into one pickable
              queue order. If Smoke Drive hits a Storage CORS block, use Upload PDF until
              the bucket CORS from repo root storage-cors.json is applied.
            </p>
          </div>
          <div className="wave-desk__carriers-actions">
            <label className="wave-desk__upload">
              <UploadOutlined /> Upload PDF
              <input
                type="file"
                accept="application/pdf,.pdf"
                multiple
                disabled={Boolean(busy)}
                onChange={(event) => {
                  const files = Array.from(event.target.files || []);
                  (async () => {
                    for (const file of files) {
                      try {
                        await loadCarrierBlob(file, file.name);
                      } catch (error) {
                        setStatus(error?.message || 'Could not parse that PDF.');
                        setBusy('');
                        break;
                      }
                    }
                  })();
                  event.target.value = '';
                }}
              />
            </label>
            <button
              type="button"
              className="toolkit-btn toolkit-btn--ghost"
              disabled={Boolean(busy) || !user?.uid}
              onClick={() => setDrivePickerOpen(true)}
            >
              <FolderOpenOutlined /> Smoke Drive
            </button>
            {carrierAttachments.length ? (
              <button
                type="button"
                className="toolkit-btn toolkit-btn--ghost"
                disabled={Boolean(busy)}
                onClick={() => {
                  clearAllCarriers().catch(() => {});
                  setStatus('Cleared attached carrier PDFs.');
                }}
              >
                Clear all PDFs
              </button>
            ) : null}
          </div>
        </div>

        {carrierAttachments.map((attachment) => (
          <div key={attachment.id} className="wave-desk__carrier-doc">
            <div className="wave-desk__carrier-meta">
              <div>
                <b>{attachment.name}</b>
                <span>
                  {attachment.pageCount} page{attachment.pageCount === 1 ? '' : 's'} ·{' '}
                  {
                    Object.values(attachment.pageAssignments || {}).filter((value) =>
                      String(value || '').trim(),
                    ).length
                  }{' '}
                  assigned
                </span>
              </div>
              <button
                type="button"
                className="toolkit-btn toolkit-btn--ghost"
                disabled={Boolean(busy)}
                onClick={() => {
                  removeCarrierAttachment(attachment.id).catch(() => {});
                }}
              >
                Remove
              </button>
            </div>
            <div className="wave-desk__carrier-align">
              {attachmentNeedsAlign(attachment) ? (
                <button
                  type="button"
                  className="toolkit-btn toolkit-btn--secondary"
                  disabled={Boolean(busy)}
                  onClick={() => alignAttachmentToBestMatches(attachment.id)}
                  title="Fill empty pages from the parser best match"
                >
                  Align to best matches
                </button>
              ) : null}
              <button
                type="button"
                className="toolkit-btn toolkit-btn--primary"
                disabled={
                  Boolean(busy) ||
                  !user?.uid ||
                  !attachmentFullyAssigned(attachment)
                }
                onClick={() => {
                  saveAttachmentAsTeamAttach(attachment.id).catch(() => {});
                }}
                title="Save page→order alignment for Packers and Printing Toolkit chips"
              >
                <LinkOutlined />{' '}
                {busy === 'attach'
                  ? 'Saving…'
                  : attachmentFullyAssigned(attachment)
                    ? 'Save team attach'
                    : 'Save team attach (assign pages first)'}
              </button>
              <small>
                {attachmentAssignedCount(attachment)}/{attachment.pageCount || 0}{' '}
                assigned
                {attachmentFullyAssigned(attachment)
                  ? ' · ready to align Pack list'
                  : attachmentNeedsAlign(attachment)
                    ? ' · best match available'
                    : ''}
              </small>
            </div>
            {(attachment.pageMatches || []).length > 0 ? (
              <div className="wave-desk__carrier-matches">
                {attachment.pageMatches.map((page) => (
                  <label key={`${attachment.id}-${page.pageNumber}`}>
                    <span>
                      <b>Page {page.pageNumber}</b>
                      <small>
                        {page.status} · {page.textPreview || 'No readable page text'}
                      </small>
                      <small>{page.matchSummary}</small>
                    </span>
                    <select
                      value={attachment.pageAssignments?.[page.pageNumber] || ''}
                      onChange={(event) => {
                        const nextValue = event.target.value;
                        setCarrierAttachments((previous) =>
                          previous.map((entry) =>
                            entry.id === attachment.id
                              ? {
                                  ...entry,
                                  pageAssignments: {
                                    ...entry.pageAssignments,
                                    [page.pageNumber]: nextValue,
                                  },
                                }
                              : entry,
                          ),
                        );
                      }}
                    >
                      <option value="">Select order…</option>
                      {orders.map((order) => {
                        const key = getWaveOrderKey(order);
                        const company =
                          order?.shipping?.company ||
                          order?.customer?.company ||
                          order?.companyName ||
                          '';
                        return (
                          <option key={key} value={key}>
                            {[`Order ${key}`, company].filter(Boolean).join(' · ')}
                          </option>
                        );
                      })}
                    </select>
                  </label>
                ))}
              </div>
            ) : null}
          </div>
        ))}
      </div>

      {showCommandBar ? (
        <div className="wave-desk__actions">
          <button
            type="button"
            className="toolkit-btn toolkit-btn--primary"
            disabled={!wave.orderCount || Boolean(busy)}
            onClick={() => assembleDayStack({ startImmediately: false })}
          >
            <PrinterOutlined />{' '}
            {busy === 'assemble' ? 'Assembling…' : WAVE_COMMAND_LABELS.assembleDay}
          </button>
          <button
            type="button"
            className="toolkit-btn toolkit-btn--secondary"
            disabled={!wave.orderCount || Boolean(busy)}
            onClick={() => assembleDayStack({ startImmediately: true })}
          >
            {WAVE_COMMAND_LABELS.assembleAndStart}
          </button>
          <button
            type="button"
            className="toolkit-btn toolkit-btn--ghost"
            disabled={!wave.orderCount}
            onClick={printThisWave}
          >
            {WAVE_COMMAND_LABELS.printWave}
          </button>
          <button
            type="button"
            className="toolkit-btn toolkit-btn--ghost"
            disabled={!wave.orderCount || Boolean(busy)}
            onClick={printPickListNow}
          >
            <UnorderedListOutlined />{' '}
            {busy === 'pick' ? 'Printing pick list…' : WAVE_COMMAND_LABELS.printPickList}
          </button>
        </div>
      ) : null}
      {status ? <p className="wave-desk__status">{status}</p> : null}

      <div className="wave-desk__table-wrap">
        <table className="wave-desk__table">
          <thead>
            <tr>
              <th>Qty</th>
              <th>Product</th>
              <th>Orders</th>
              <th>Goes to</th>
            </tr>
          </thead>
          <tbody>
            {wave.lines.length ? (
              wave.lines.map((line) => (
                <tr key={line.sku}>
                  <td className="wave-desk__qty">{line.quantity}</td>
                  <td>
                    <b>{line.name}</b>
                    <small>
                      {line.sku}
                      {line.packSize ? ` · ${line.packSize}` : ''}
                    </small>
                    {line.lots.length ? <small>Lot {line.lots.join(', ')}</small> : null}
                  </td>
                  <td>{line.orderCount}</td>
                  <td>
                    {line.orders.slice(0, 4).map((entry) => (
                      <span key={`${line.sku}-${entry.orderKey}`}>
                        #{entry.orderKey}
                        {entry.company ? ` ${entry.company}` : ''} ×{entry.quantity}
                      </span>
                    ))}
                    {line.orders.length > 4 ? <span>+{line.orders.length - 4} more</span> : null}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={4}>Load a folder (or select orders) to build the wave pick list.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <OperatorDrivePicker
        uid={user?.uid}
        open={drivePickerOpen}
        onClose={() => setDrivePickerOpen(false)}
        pdfOnly
        multiple
        title="Day shipping-label PDFs"
        hint="Load one or more carrier PDFs from Smoke Drive into this wave for page matching."
        confirmLabel="Use these PDFs"
        onPick={loadCarrierFromDrive}
      />
    </section>
  );
}

export default forwardRef(WaveDeskPanel);
